import React, { useState } from "react";
import { isEqual } from "lodash";
import classNames from "classnames";

import Button from "components/Button";
import Checkbox from "components/Input/Checkbox";
import Select from "components/Input/Select";
import InfoDropdown from "components/InfoDropdown";
import LoadingDots from "components/LoadingDots";

import classes from "../style.module.scss";

const ecmSyncIssueDescription = (issue) => {
    const base = `${issue.packageName}: ${issue.labelText}`;
    if (issue.reason === "package_id_mismatch") {
        return `${base} — ECM row points to a different package; re-apply, edit, or remove this upgrade.`;
    }
    if (issue.reason === "missing_category") {
        return `${base} — measure category is missing; edit or remove this row.`;
    }
    if (issue.reason === "missing_ecm_row") {
        return `${base} — not represented in the ECM map; re-apply, edit, or remove.`;
    }
    return `${base} — re-apply, edit, or remove this upgrade.`;
};

const SimulationInfo = React.memo(
    ({
        runInfo,
        handleCommunityParametricRunClick,
        isSimulationRunning,
        isPreparingRun,
        hasMissingCodes,
        ecmTableSyncIssues = [],
        validationComboCount = 0,
        validationComboMax = 250,
        validationComboOutOfRange = false,
        communityRunBlocked,
        readyModelOptions,
        selectedValidationModelId,
        onValidationModelChange,
        validationModelCaption,
        measureStats,
        disableValidate,
        validationInProgress,
        validationControlsLocked = false,
        onValidateClick,
    }) => {
        const [isConfirmed, toggleIsConfirmed] = useState(false);

        const { numFailed, numToBeRun, totalSimulations, failedModelInfo } = runInfo;

        const runBlocked = communityRunBlocked || false;

        const { totalNonNone, passingCount, failingLabels, unvalidatedLabels = [], measuresAllPassing } = measureStats;

        const selectOptions = readyModelOptions.map(({ id, name }) => ({
            value: id,
            label: name,
        }));

        const hasEcmTableSyncIssues = ecmTableSyncIssues.length > 0;

        const showSimulationReadyNotice =
            measuresAllPassing &&
            !hasMissingCodes &&
            !hasEcmTableSyncIssues &&
            !validationComboOutOfRange &&
            !validationInProgress;

        return (
            <div className={`${classes.section} ${classes.border}`}>
                <div className={classes.simulationLayout}>
                    <div className={classes.simulationLeft}>
                        <div className={classes.simulationLeftLine}>
                            <span>
                                <b>Measures passing validation:</b> {passingCount} / {totalNonNone}
                                {totalNonNone === 0 ? (
                                    <span className={classes.validationHint}> (add upgrades in Upgrades)</span>
                                ) : null}
                            </span>
                        </div>

                        {validationModelCaption ? (
                            <div className={classes.validationCaptionRow}>
                                <p
                                    className={
                                        validationInProgress
                                            ? classes.validationCaptionProgress
                                            : classes.validationCaption
                                    }
                                >
                                    {validationModelCaption}
                                    {validationInProgress ? (
                                        <span className={classes.validationCaptionLoaderWrap}>
                                            <LoadingDots small color="#0049c6" />
                                        </span>
                                    ) : null}
                                </p>
                            </div>
                        ) : null}

                        <p className={classes.simulationLeftLine}>
                            <b>Simulations to be run:</b> {numToBeRun}
                        </p>
                        <p className={classes.simulationLeftLine}>
                            <b>Total simulations{numToBeRun === 0 ? "" : " (after run)"}:</b> {totalSimulations}
                        </p>

                        {!!numFailed && (
                            <InfoDropdown isError label={`${numFailed} simulations failed`}>
                                {Object.entries(failedModelInfo).map(([modelId, { modelName, upgradePackages }]) =>
                                    upgradePackages.map((packageName, index) => (
                                        <div
                                            key={`${modelId}-package-${index}`}
                                            className={classes.resultErrorListItem}
                                        >
                                            <p>
                                                <b>Model:</b> {modelName}
                                            </p>
                                            <p>
                                                <b>Package:</b> {packageName}
                                            </p>
                                        </div>
                                    ))
                                )}
                            </InfoDropdown>
                        )}
                    </div>

                    <div className={classes.simulationNotices}>
                        {hasMissingCodes ? (
                            <InfoDropdown
                                className={classes.simulationNoticeDropdown}
                                isWarning
                                label={"Please review codes in Upgrades tab"}
                                contentClassName={classes.codeWarningContent}
                            >
                                <p>
                                    An error occurred trying to locate missing code data. Please re-select the desired
                                    code from the drop-down list and save your changes.
                                </p>
                            </InfoDropdown>
                        ) : null}
                        {hasEcmTableSyncIssues ? (
                            <InfoDropdown
                                className={classes.simulationNoticeDropdown}
                                isWarning
                                label={`${ecmTableSyncIssues.length} upgrade(s) out of sync with ECM map`}
                                contentClassName={classes.codeWarningContent}
                            >
                                <p className={classes.simulationReadyNoticeBody}>
                                    The upgrade table and community ECM map do not match. Fix the following in the
                                    Upgrades tab (re-apply the measure, edit it, or delete the row), then save:
                                </p>
                                <ul className={classes.failedMeasureList}>
                                    {ecmTableSyncIssues.map((issue, idx) => (
                                        <li key={`ecm-sync-${issue.packageId}-${issue.upgradeId}-${idx}`}>
                                            {ecmSyncIssueDescription(issue)}
                                        </li>
                                    ))}
                                </ul>
                            </InfoDropdown>
                        ) : null}
                        {validationComboOutOfRange ? (
                            <InfoDropdown
                                className={classes.simulationNoticeDropdown}
                                isWarning
                                label={
                                    validationComboCount > validationComboMax
                                        ? "Validation batch too large"
                                        : "No validation combinations"
                                }
                                contentClassName={classes.codeWarningContent}
                            >
                                {validationComboCount > validationComboMax ? (
                                    <p className={classes.simulationReadyNoticeBody}>
                                        Validation would run {validationComboCount} single-upgrade combinations. You must
                                        define a smaller batch than {validationComboMax} for validation purposes.
                                        Reduce active measures or packages, then try again.
                                    </p>
                                ) : (
                                    <p className={classes.simulationReadyNoticeBody}>
                                        There are no validation combinations to run with the current ECM configuration.
                                    </p>
                                )}
                            </InfoDropdown>
                        ) : null}
                        {failingLabels.length > 0 ? (
                            <InfoDropdown
                                className={classes.simulationNoticeDropdown}
                                isError
                                label={`${failingLabels.length} measure(s) failed`}
                            >
                                <ul className={classes.failedMeasureList}>
                                    {failingLabels.map((label, idx) => (
                                        <li key={`${label}-${idx}`}>{label}</li>
                                    ))}
                                </ul>
                            </InfoDropdown>
                        ) : null}
                        {unvalidatedLabels.length > 0 ? (
                            <InfoDropdown
                                className={classes.simulationNoticeDropdown}
                                isWarning
                                label={`${unvalidatedLabels.length} upgrade(s) not yet validated`}
                            >
                                <ul className={classes.failedMeasureList}>
                                    {unvalidatedLabels.map((label, idx) => (
                                        <li key={`unvalidated-${label}-${idx}`}>{label}</li>
                                    ))}
                                </ul>
                            </InfoDropdown>
                        ) : null}
                        {showSimulationReadyNotice ? (
                            <InfoDropdown
                                className={classes.simulationNoticeDropdown}
                                isSuccess
                                label="Simulation is ready to run"
                            >
                                <p className={classes.simulationReadyNoticeBody}>
                                    All ECM measures have passed validation. When you are ready, confirm details and run
                                    simulations.
                                </p>
                            </InfoDropdown>
                        ) : null}
                    </div>

                    <div className={classes.simulationRight}>
                        <div className={classes.simulationRightGridRow}>
                            <div className={classes.commConfigControlWrap}>
                                <Select
                                    name="community-validation-model"
                                    label="Validation model"
                                    className={classes.commConfigSelect}
                                    options={selectOptions}
                                    disabled={selectOptions.length === 0 || validationControlsLocked}
                                    placeholder={selectOptions.length === 0 ? "No ready models" : "Select"}
                                    input={{
                                        value: selectedValidationModelId,
                                        onChange: (v) =>
                                            onValidationModelChange(
                                                typeof v === "object" && v != null && "value" in v ? v.value : v || ""
                                            ),
                                    }}
                                />
                            </div>
                            <Button
                                type="hollow"
                                className={classes.commConfigActionBtn}
                                disabled={disableValidate || validationControlsLocked}
                                onClick={onValidateClick}
                            >
                                Validate upgrades
                            </Button>
                        </div>

                        <div className={classes.simulationRightGridRowConfirm}>
                            <Checkbox
                                label="Confirm Details"
                                input={{
                                    onChange: () => toggleIsConfirmed((prev) => !prev),
                                    value: isConfirmed,
                                }}
                                disabled={
                                    isPreparingRun ||
                                    isSimulationRunning ||
                                    numToBeRun === 0 ||
                                    hasMissingCodes ||
                                    hasEcmTableSyncIssues ||
                                    runBlocked ||
                                    validationControlsLocked
                                }
                                large
                            />
                            <Button
                                className={classes.commConfigActionBtn}
                                disabled={
                                    !isConfirmed ||
                                    isPreparingRun ||
                                    isSimulationRunning ||
                                    runBlocked ||
                                    validationControlsLocked
                                }
                                onClick={handleCommunityParametricRunClick}
                            >
                                {isSimulationRunning
                                    ? "Running Simulations..."
                                    : isPreparingRun
                                      ? "Preparing Run..."
                                      : "Run Simulations"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
    isEqual
);

export default SimulationInfo;

export const TableHeader = ({ children, gridTemplateColumns = "" }) => {
    return (
        <li className={`${classes.item} ${classes.head}`} style={{ gridTemplateColumns: gridTemplateColumns }}>
            {children}
        </li>
    );
};

export const TableRow = ({ children, id, disabled, gridTemplateColumns = "" }) => {
    return (
        <li
            className={classNames(classes.item, { [classes.disabled]: disabled })}
            id={id}
            style={{ gridTemplateColumns: gridTemplateColumns }}
        >
            {children}
        </li>
    );
};

export const Section = ({ title, subtitle, children }) => {
    return (
        <div className={classes.section}>
            <h3 className={classes.title}> {title} </h3>
            <div>{children}</div>
            {subtitle && <p className={classes.subtitle}> {subtitle} </p>}
        </div>
    );
};
